import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { format } from 'date-fns';
import { CalendarIcon, Plus, Trash2, Upload, Download, AlertCircle, CheckCircle, MapPin, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { downloadFile, downloadDirect } from '../utils/download';

const API = '';

export function PONew() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  // Manual entry state
  const [poNumber, setPoNumber] = useState('');
  const [buyerId, setBuyerId] = useState('');
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [orderDate, setOrderDate] = useState(null);
  const itemIdRef = useRef(1);
  const [lineItems, setLineItems] = useState([{ _key: 0, sku: '', brand: '', size: '', ordered_qty: '', rate: '' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Bulk upload state
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const [bulkBuyerId, setBulkBuyerId] = useState('');
  const [bulkSelectedBuyer, setBulkSelectedBuyer] = useState(null);

  // Fetch buyers from Buyer Master
  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        const { data } = await axios.get(`${API}/api/buyers-master`, { withCredentials: true });
        setBuyers(data);
      } catch { toast.error('Failed to load buyers'); }
    };
    fetchBuyers();
  }, []);

  const handleBuyerSelect = (id, isBulk = false) => {
    const buyer = buyers.find(b => b.id === id);
    if (isBulk) {
      setBulkBuyerId(id);
      setBulkSelectedBuyer(buyer || null);
    } else {
      setBuyerId(id);
      setSelectedBuyer(buyer || null);
    }
  };

  const addLineItem = () => {
    setLineItems([...lineItems, { _key: itemIdRef.current++, sku: '', brand: '', size: '', ordered_qty: '', rate: '' }]);
  };

  const removeLineItem = (index) => {
    if (lineItems.length > 1) {
      setLineItems(lineItems.filter((_, i) => i !== index));
    }
  };

  const updateLineItem = (index, field, value) => {
    const updated = [...lineItems];
    updated[index][field] = value;
    setLineItems(updated);
  };

  const handleManualSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!poNumber || !buyerId || !orderDate || lineItems.some(item => !item.sku || !item.size || !item.ordered_qty)) {
      setError('Please fill all required fields. Buyer selection is mandatory.');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/api/po`, {
        po_number: poNumber,
        buyer_id: buyerId,
        order_date: format(orderDate, 'yyyy-MM-dd'),
        line_items: lineItems.map(item => ({
          sku: item.sku,
          brand: item.brand || '',
          size: item.size,
          ordered_qty: parseInt(item.ordered_qty),
          rate: parseFloat(item.rate) || 0
        }))
      }, { withCredentials: true });

      toast.success('PO created successfully');
      navigate('/po/list');
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create PO');
    } finally {
      setLoading(false);
    }
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0] || e.target?.files?.[0];
    if (file && (file.name.endsWith('.xlsx') || file.name.endsWith('.xls') || file.name.endsWith('.csv'))) {
      setUploadFile(file);
      setUploadResult(null);
    } else {
      toast.error('Please upload an Excel (.xlsx, .xls) or CSV file');
    }
  };

  const handleBulkUpload = async () => {
    if (!uploadFile) return;
    if (!bulkBuyerId) {
      toast.error('Please select a buyer before uploading');
      return;
    }
    
    setUploadLoading(true);
    setUploadResult(null);
    
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('buyer_id', bulkBuyerId);
      
      const { data } = await axios.post(`${API}/api/po/bulk-upload`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setUploadResult(data);
      toast.success(data.message);
      
      if (data.errors.length === 0) {
        setTimeout(() => navigate('/po/list'), 2000);
      }
    } catch (e) {
      setUploadResult({ message: 'Upload failed', errors: [e.response?.data?.detail || e.message] });
      toast.error('Upload failed');
    } finally {
      setUploadLoading(false);
    }
  };

  const downloadTemplate = async () => {
    setTemplateDownloading(true);
    toast.info('Starting download...');
    
    try {
      // Use direct download for template (no auth required)
      downloadDirect('/api/reports/template', 'po_upload_template.xlsx');
      toast.success('Template download started!');
    } catch (e) {
      toast.error('Download failed. Please try again.');
    } finally {
      setTimeout(() => setTemplateDownloading(false), 1000);
    }
  };

  return (
    <div className="p-6" data-testid="po-new-page">
      {/* Header */}
      <div className="mb-6">
        <h1 className="font-heading font-black text-3xl tracking-tighter text-[#09090B]">Create Purchase Order</h1>
        <p className="text-sm text-zinc-500">Add a new PO manually or upload in bulk</p>
      </div>

      <Tabs defaultValue="manual" className="w-full">
        <TabsList className="mb-6 bg-zinc-100 rounded-sm p-1">
          <TabsTrigger value="manual" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-manual">
            Manual Entry
          </TabsTrigger>
          <TabsTrigger value="bulk" className="rounded-sm data-[state=active]:bg-white" data-testid="tab-bulk">
            Bulk Upload
          </TabsTrigger>
        </TabsList>

        {/* Manual Entry Tab */}
        <TabsContent value="manual">
          <form onSubmit={handleManualSubmit} className="bg-white border border-zinc-200 p-6">
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="form-error">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}

            {/* Master Data */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4 pb-4 border-b border-zinc-200">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">PO Number *</Label>
                <Input
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="PO-2026-001"
                  className="mt-1 h-9 rounded-sm font-mono"
                  required
                  data-testid="po-number-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Buyer *</Label>
                <Select value={buyerId} onValueChange={(v) => handleBuyerSelect(v)} data-testid="buyer-select">
                  <SelectTrigger className="mt-1 h-9 rounded-sm" data-testid="buyer-select-trigger">
                    <SelectValue placeholder="Select buyer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {buyers.map(b => (
                      <SelectItem key={b.id} value={b.id} data-testid={`buyer-option-${b.id}`}>
                        {b.buyer_name}
                      </SelectItem>
                    ))}
                    {buyers.length === 0 && (
                      <div className="p-3 text-xs text-zinc-400 text-center">No buyers. Add via Buyer Master.</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Order Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full mt-1 h-9 rounded-sm justify-start text-left font-normal"
                      data-testid="order-date-trigger"
                    >
                      <CalendarIcon size={14} className="mr-2" />
                      {orderDate ? format(orderDate, 'yyyy-MM-dd') : 'Select date'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0" align="start">
                    <Calendar
                      mode="single"
                      selected={orderDate}
                      onSelect={setOrderDate}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>

            {/* Buyer Preview */}
            {selectedBuyer && (
              <div className="mb-4 p-3 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid="buyer-preview">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                  <div>
                    <span className="text-zinc-400 uppercase tracking-wider text-[10px]">Buyer</span>
                    <p className="font-medium mt-0.5">{selectedBuyer.buyer_name}</p>
                  </div>
                  <div>
                    <span className="text-zinc-400 uppercase tracking-wider text-[10px]">GST</span>
                    <p className="font-mono mt-0.5">{selectedBuyer.gst_number || '-'}</p>
                  </div>
                  <div className="flex items-start gap-1">
                    <MapPin size={12} className="text-zinc-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-zinc-400 uppercase tracking-wider text-[10px]">Billing</span>
                      <p className="mt-0.5">{selectedBuyer.billing_address || '-'}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-1">
                    <Phone size={12} className="text-zinc-400 mt-0.5 shrink-0" />
                    <div>
                      <span className="text-zinc-400 uppercase tracking-wider text-[10px]">Contact</span>
                      <p className="mt-0.5">{selectedBuyer.contact_person || '-'} {selectedBuyer.phone ? `(${selectedBuyer.phone})` : ''}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Line Items */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Line Items</Label>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={addLineItem}
                  className="h-7 rounded-sm text-xs"
                  data-testid="add-line-item"
                >
                  <Plus size={12} className="mr-1" />
                  Add Row
                </Button>
              </div>

              <div className="border border-zinc-200 rounded-sm overflow-x-auto">
                <table className="w-full text-sm min-w-[700px]">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">SKU / Article</th>
                      <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">Brand</th>
                      <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-20">Size</th>
                      <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-24">Ordered Qty</th>
                      <th className="text-left p-2 text-[10px] uppercase tracking-wider text-zinc-500 font-semibold w-24">Rate (₹)</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineItems.map((item, index) => (
                      <tr key={item._key} className="border-t border-zinc-200">
                        <td className="p-2">
                          <Input
                            value={item.sku}
                            onChange={(e) => updateLineItem(index, 'sku', e.target.value)}
                            placeholder="SKU001"
                            className="h-8 rounded-sm font-mono text-sm"
                            required
                            data-testid={`line-sku-${index}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={item.brand}
                            onChange={(e) => updateLineItem(index, 'brand', e.target.value)}
                            placeholder="Nike"
                            className="h-8 rounded-sm text-sm"
                            data-testid={`line-brand-${index}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            value={item.size}
                            onChange={(e) => updateLineItem(index, 'size', e.target.value)}
                            placeholder="42"
                            className="h-8 rounded-sm font-mono text-sm"
                            required
                            data-testid={`line-size-${index}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            value={item.ordered_qty}
                            onChange={(e) => updateLineItem(index, 'ordered_qty', e.target.value)}
                            placeholder="100"
                            className="h-8 rounded-sm font-mono text-sm"
                            min="1"
                            required
                            data-testid={`line-qty-${index}`}
                          />
                        </td>
                        <td className="p-2">
                          <Input
                            type="number"
                            step="0.01"
                            value={item.rate}
                            onChange={(e) => updateLineItem(index, 'rate', e.target.value)}
                            placeholder="1500.00"
                            className="h-8 rounded-sm font-mono text-sm"
                            min="0"
                            data-testid={`line-rate-${index}`}
                          />
                        </td>
                        <td className="p-2">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeLineItem(index)}
                            className="h-8 w-8 p-0 text-zinc-400 hover:text-red-500"
                            disabled={lineItems.length === 1}
                            data-testid={`remove-line-${index}`}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-3 mt-6 pt-6 border-t border-zinc-200">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/po/list')}
                className="h-9 rounded-sm"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="h-9 rounded-sm bg-[#09090B] hover:bg-[#27272A]"
                disabled={loading}
                data-testid="submit-po"
              >
                {loading ? 'Creating...' : 'Create PO'}
              </Button>
            </div>
          </form>
        </TabsContent>

        {/* Bulk Upload Tab */}
        <TabsContent value="bulk">
          <div className="bg-white border border-zinc-200 p-6">
            {/* Step 1: Select Buyer */}
            <div className="mb-6 pb-6 border-b border-zinc-200">
              <h3 className="font-medium text-[#09090B] mb-1">Step 1: Select Buyer *</h3>
              <p className="text-sm text-zinc-500 mb-3">All POs in the uploaded file will be assigned to this buyer</p>
              <div className="max-w-sm">
                <Select value={bulkBuyerId} onValueChange={(v) => handleBuyerSelect(v, true)} data-testid="bulk-buyer-select">
                  <SelectTrigger className="h-9 rounded-sm" data-testid="bulk-buyer-select-trigger">
                    <SelectValue placeholder="Select buyer..." />
                  </SelectTrigger>
                  <SelectContent>
                    {buyers.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.buyer_name}</SelectItem>
                    ))}
                    {buyers.length === 0 && (
                      <div className="p-3 text-xs text-zinc-400 text-center">No buyers. Add via Buyer Master.</div>
                    )}
                  </SelectContent>
                </Select>
              </div>
              {bulkSelectedBuyer && (
                <div className="mt-3 p-2 bg-zinc-50 border border-zinc-200 rounded-sm text-xs" data-testid="bulk-buyer-preview">
                  <span className="font-medium">{bulkSelectedBuyer.buyer_name}</span>
                  {bulkSelectedBuyer.gst_number && <span className="ml-3 text-zinc-500">GST: {bulkSelectedBuyer.gst_number}</span>}
                  {bulkSelectedBuyer.billing_address && <span className="ml-3 text-zinc-500">{bulkSelectedBuyer.billing_address.substring(0, 40)}</span>}
                </div>
              )}
            </div>

            {/* Step 2: Download Template */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-6 pb-6 border-b border-zinc-200">
              <div>
                <h3 className="font-medium text-[#09090B]">Step 2: Download Template</h3>
                <p className="text-sm text-zinc-500">Use our Excel template to prepare your PO data (no Buyer column needed)</p>
              </div>
              <Button
                variant="outline"
                onClick={downloadTemplate}
                className="h-9 rounded-sm"
                disabled={templateDownloading}
                data-testid="download-template"
              >
                {templateDownloading ? (
                  <>
                    <span className="w-4 h-4 border-2 border-zinc-400 border-t-transparent rounded-full animate-spin mr-2"></span>
                    Downloading...
                  </>
                ) : (
                  <>
                    <Download size={14} className="mr-2" />
                    Download Template
                  </>
                )}
              </Button>
              <a
                href={`${API}/api/reports/template`}
                download="po_upload_template.xlsx"
                className="text-xs text-zinc-500 hover:text-zinc-700 underline ml-2"
                target="_blank"
                rel="noopener noreferrer"
              >
                (or click here)
              </a>
            </div>

            {/* Step 3: Upload Zone */}
            <div
              className={`upload-zone p-8 text-center cursor-pointer ${dragOver ? 'dragover' : ''} ${!bulkBuyerId ? 'opacity-50 pointer-events-none' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={handleFileDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-zone"
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                onChange={handleFileDrop}
                className="hidden"
              />
              <Upload size={32} className="mx-auto mb-3 text-zinc-400" />
              <p className="text-sm text-zinc-600 mb-1">
                {uploadFile ? uploadFile.name : 'Drag and drop your Excel file here'}
              </p>
              <p className="text-xs text-zinc-400">or click to browse • .xlsx, .xls, .csv</p>
            </div>

            {/* Upload Button */}
            {uploadFile && (
              <div className="mt-4 flex justify-center">
                <Button
                  onClick={handleBulkUpload}
                  disabled={uploadLoading}
                  className="h-9 rounded-sm bg-[#09090B] hover:bg-[#27272A]"
                  data-testid="upload-button"
                >
                  {uploadLoading ? 'Uploading...' : 'Upload & Create POs'}
                </Button>
              </div>
            )}

            {/* Upload Result */}
            {uploadResult && (
              <div className="mt-6 p-4 border border-zinc-200 rounded-sm" data-testid="upload-result">
                <div className="flex items-center gap-2 mb-2">
                  {uploadResult.errors?.length === 0 ? (
                    <CheckCircle size={16} className="text-green-600" />
                  ) : (
                    <AlertCircle size={16} className="text-amber-600" />
                  )}
                  <span className="font-medium">{uploadResult.message}</span>
                </div>
                {uploadResult.errors?.length > 0 && (
                  <ul className="text-sm text-zinc-600 ml-6 list-disc">
                    {uploadResult.errors.map((err, i) => (
                      <li key={`${i}-${err}`}>{err}</li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
